import { ChangeDetectionStrategy, Component, DestroyRef, computed, inject, input, OnInit, output, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NgClass } from '@angular/common';
import { AssetsApiService } from 'src/app/core/http/services/assets.service';
import { DataCopilotApiService } from 'src/app/core/http/services/data-copilot.service';
import { AssetFlatListTableComponent } from 'src/app/shared/common-ui-components/asset-table/asset-flatlist-table.component';
import { catchError, finalize, map, of } from 'rxjs';
import { UIIconsComponent } from 'src/app/shared/ui/icons/icons.component';
import { UISkeletonTableComponent } from 'src/app/shared/ui/skeletons/table/skeleton-table.component';

interface ConversationQuestion {
  id: string;
  text: string;
  conversationId: string;
  disabled?: boolean;
}

@Component({
  selector: 'app-add-insights-modal',
  standalone: true,
  imports: [
    NgClass,
    UIIconsComponent,
    AssetFlatListTableComponent,
    UISkeletonTableComponent
  ],
  templateUrl: './add-insights-modal.component.html',
  styleUrl: './add-insights-modal.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class AddInsightsModalComponent implements OnInit {
  isOpen = input<boolean>(false);
  canvasId = input<string>('');
  canvasCards = input<any[]>([]);
  closed = output<void>();
  insightsSelected = output<any>();

  private assetsApiService = inject(AssetsApiService);
  private dataCopilotApiService = inject(DataCopilotApiService);
  private destroyRef = inject(DestroyRef);

  listType = signal<string>('category');
  activeTab = signal<'reports' | 'conversations'>('reports');
  reports = signal<any[]>([]);
  conversations = signal<any[]>([]);
  expandedConversations = signal<Set<string>>(new Set());
  loadingConversationQuestions = signal<Map<string, boolean>>(new Map());
  conversationQuestionsMap = signal<Map<string, ConversationQuestion[]>>(new Map());
  loadingReports = signal<boolean>(true);
  loadingConversations = signal<boolean>(false);
  selectedReport = signal<any>(null);
  selectedQuestion = signal<ConversationQuestion | null>(null);
  isGeneratingInsights = signal<boolean>(false);
  insightsError = signal<string>('');

  filteredReports = computed(() => {
    const cards = this.canvasCards();
    const reports = this.reports();
    if (!cards || cards.length === 0) return [];
    const canvasReportIds = cards
      .filter(card => card.cardType === 'REPORT' && card.reportId)
      .map(card => card.reportId);
    return reports.filter(report => canvasReportIds.includes(report.id));
  });

  filteredConversations = computed(() => {
    const cards = this.canvasCards();
    const conversations = this.conversations();
    if (!cards || cards.length === 0) return [];
    const canvasConversationIds = cards
      .filter(card => card.cardType === 'ANSWER' && card.conversationId)
      .map(card => card.conversationId);
    return conversations.filter(c => canvasConversationIds.includes(c.id));
  });

  ngOnInit(): void {
    this.loadReports();
  }

  switchTab(tab: 'reports' | 'conversations'): void {
    this.activeTab.set(tab);
    if (tab === 'reports' && this.reports().length === 0) {
      this.loadReports();
    } else if (tab === 'conversations' && this.conversations().length === 0) {
      this.loadConversations();
    }
  }

  loadReports(): void {
    this.loadingReports.set(true);

    this.assetsApiService
      .getAssetByType('REPORT', 'network-only')
      .pipe(
        map((response) =>
          response.data.assetsByType.map((model: any) => ({
            id: model.id,
            name: model.name,
            description: model.description,
            tags: model.tags,
            parameters: model.parameters,
            updatedAt: model.updatedAt
          }))
        ),
        catchError((error) => {
          console.error('Error getting saved reports:', error);
          return of([]);
        }),
        finalize(() => this.loadingReports.set(false))
      )
      .subscribe({
        next: (reports) => this.reports.set(reports),
      });
  }

  loadConversations(): void {
    this.loadingConversations.set(true);

    this.assetsApiService
      .getAssetsByRelationshipsByType(['CONVERSATION'], ['DATASCOPE'], 'no-cache')
      .pipe(
        catchError((error) => {
          console.error('Error loading conversations:', error);
          return of([]);
        }),
        finalize(() => this.loadingConversations.set(false))
      )
      .subscribe({
        next: (data: any[]) => {
          this.dataCopilotApiService.setSelectedConversationDataWithMatchingDataScope(data);
          const filtered = data.filter((c: any) => !c.parameters.smartCollaboration);
          this.conversations.set(filtered);
        }
      });

    this.dataCopilotApiService.dataCopilotConversationData$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (data) => {
          if (data.conversationsData?.length > 0) {
            const filtered = data.conversationsData.filter((c: any) => !c.parameters.smartCollaboration);
            this.conversations.set(filtered);
          }
        }
      });
  }

  toggleConversation(id: string, event: Event): void {
    event.stopPropagation();

    this.expandedConversations.update(set => {
      const s = new Set(set);
      if (s.has(id)) {
        s.delete(id);
      } else {
        s.add(id);
        this.loadConversationQuestions(id);
      }
      return s;
    });
  }

  loadConversationQuestions(conversationId: string): void {
    if (this.conversationQuestionsMap().has(conversationId)) return;

    this.loadingConversationQuestions.update(map => {
      const m = new Map(map);
      m.set(conversationId, true);
      return m;
    });

    const conversation = this.conversations().find(c => c.id === conversationId);
    if (!conversation) {
      console.error('Conversation not found:', conversationId);
      this.loadingConversationQuestions.update(map => {
        const m = new Map(map);
        m.set(conversationId, false);
        return m;
      });
      return;
    }

    this.assetsApiService.getAssetsByRelationshipsById(conversationId, ['QUESTION'], 'network-only')
      .pipe(
        catchError(error => {
          console.error(`Error loading questions for conversation ${conversationId}:`, error);
          return of([]);
        }),
        finalize(() => {
          this.loadingConversationQuestions.update(map => {
            const m = new Map(map);
            m.set(conversationId, false);
            return m;
          });
        })
      )
      .subscribe({
        next: (assets: any[]) => {
          const allQuestions = assets[0]?.parameters?.QUESTIONS
            ?.filter((asset: any) => asset.type === 'QUESTION' && asset?.parameters?.INPUT_TEXT && asset?.parameters?.GENERATED_SQL && asset?.parameters?.SECURED_SQL)
            .map((asset: any) => ({
              id: asset.id,
              text: asset.parameters.INPUT_TEXT || asset.parameters.MESSAGE || 'No question text',
              conversationId,
              parameters: asset.parameters || {}
            })) || [];

          const canvasQuestionIds = this.canvasCards()
            .filter(card => card.cardType === 'ANSWER' && card.questionId && card.conversationId === conversationId)
            .map(card => card.questionId);

          const filteredQuestions = allQuestions.filter((q: any) => canvasQuestionIds.includes(q.id));

          this.conversationQuestionsMap.update(map => {
            const m = new Map(map);
            m.set(conversationId, filteredQuestions);
            return m;
          });
        }
      });
  }

  isConversationExpanded(id: string): boolean {
    return this.expandedConversations().has(id);
  }

  isQuestionLoading(conversationId: string): boolean {
    return this.loadingConversationQuestions().get(conversationId) === true;
  }

  hasQuestions(conversationId: string): boolean {
    const questions = this.conversationQuestionsMap().get(conversationId);
    return questions !== undefined && questions.length > 0;
  }

  getQuestionsForConversation(conversationId: string): ConversationQuestion[] {
    return this.conversationQuestionsMap().get(conversationId) || [];
  }

  isQuestionSelected(questionId: string): boolean {
    return this.selectedQuestion()?.id === questionId;
  }

  selectQuestion(question: ConversationQuestion, event: Event): void {
    event.stopPropagation();

    if (this.selectedQuestion()?.id === question.id) {
      this.selectedQuestion.set(null);
    } else {
      this.selectedQuestion.set(question);
      this.selectedReport.set(null);
    }
  }

  public changeListType(type: string): void {
    this.listType.set(type);
  }

  public onChangeListType(type: string): void {
    this.listType.set(type);
  }

  handleReportSelection(selectedAssets: any[]): void {
    selectedAssets.forEach(asset => asset.type = 'REPORT');

    if (selectedAssets.length > 0) {
      this.selectedReport.set(selectedAssets[0]);
      this.selectedQuestion.set(null);
    } else {
      this.selectedReport.set(null);
    }
  }

  addInsightsToCanvas(): void {
    if (this.selectedReport()) {
      this.isGeneratingInsights.set(true);
      this.insightsError.set('');

      this.insightsSelected.emit({
        type: 'GENERATE_INSIGHTS',
        reportId: this.selectedReport().id,
        reportName: this.selectedReport().name
      });
    } else if (this.selectedQuestion()) {
      this.isGeneratingInsights.set(true);
      this.insightsError.set('');

      this.insightsSelected.emit({
        type: 'ANSWER',
        questionId: this.selectedQuestion()!.id,
        conversationId: this.selectedQuestion()!.conversationId,
        questionText: this.selectedQuestion()!.text
      });
    }
  }

  closeModal(): void {
    this.selectedReport.set(null);
    this.selectedQuestion.set(null);
    this.isGeneratingInsights.set(false);
    this.insightsError.set('');
    this.closed.emit();
  }

  onInsightsCompleted(): void {
    this.isGeneratingInsights.set(false);
    this.closeModal();
  }

  onInsightsError(error: string): void {
    this.isGeneratingInsights.set(false);
    this.insightsError.set(error);
  }

  get hasSelection(): boolean {
    return this.selectedReport() !== null || this.selectedQuestion() !== null;
  }

  get isAddButtonDisabled(): boolean {
    return !this.hasSelection;
  }
}
